/**
 * Настройка плиток событий.
 *
 * Семьи разные: кому-то нужен «Бассейн», а «Купание» не нужно никогда.
 * Скрытая плитка исчезает только у этой семьи — системный справочник общий
 * и остаётся нетронутым.
 */

import { useState } from 'react'

import { useEventTypes } from '@/shared/api/day'
import { useCreateEventType, useSetEventTypeVisibility } from '@/shared/api/trust'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Sheet } from '@/shared/ui/Sheet'
import { eventIcon } from '@/shared/ui/icon-map'
import { Field, TextInput, Toggle } from '@/shared/ui/fields'
import { Card } from '@/shared/ui/primitives'
import { SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

// Полный системный список: чтобы показать и то, что семья уже скрыла.
// Значок берётся из набора по коду, поэтому в списке его нет.
const SYSTEM_TILES: { code: string; title: string }[] = [
  { code: 'pickup', title: 'Забрал(а)' },
  { code: 'walk', title: 'Прогулка' },
  { code: 'meal', title: 'Покормил(а)' },
  { code: 'nap', title: 'Сон' },
  { code: 'activity', title: 'Кружок' },
  { code: 'lesson', title: 'Занятие дома' },
  { code: 'bath', title: 'Купание' },
  { code: 'medicine', title: 'Лекарство' },
  { code: 'dropoff', title: 'Дома' },
  { code: 'note', title: 'Заметка' },
]

export function TilesSheet({
  open,
  familyId,
  onClose,
}: {
  open: boolean
  familyId: number
  onClose: () => void
}) {
  const [adding, setAdding] = useState(false)
  const [emoji, setEmoji] = useState('🎈')
  const [title, setTitle] = useState('')

  const types = useEventTypes(familyId)
  const visibility = useSetEventTypeVisibility(familyId, {
    onError: (error) => toast.error(error.message),
  })
  const create = useCreateEventType(familyId, {
    onSuccess: () => {
      haptic('success')
      toast.success('Плитка добавлена')
      setTitle('')
      setAdding(false)
    },
    onError: (error) => toast.error(error.message),
  })

  const visible = new Set((types.data ?? []).map((item) => item.code))
  const custom = (types.data ?? []).filter(
    (item) => !SYSTEM_TILES.some((tile) => tile.code === item.code),
  )

  return (
    <Sheet
      open={open}
      onClose={() => {
        setAdding(false)
        onClose()
      }}
      title="Плитки на экране няни"
      footer={
        adding ? (
          <Button
            block
            size="lg"
            loading={create.isPending}
            disabled={!title.trim()}
            onClick={() => create.mutate({ emoji: emoji || '🎈', title: title.trim() })}
          >
            Добавить плитку
          </Button>
        ) : (
          <Button block size="lg" variant="secondary" onClick={() => setAdding(true)}>
            + Своя плитка
          </Button>
        )
      }
    >
      {types.isLoading && <SkeletonCard lines={4} />}

      {adding && (
        <Card className="mb-4">
          <div className="flex gap-3">
            <Field label="Значок">
              <TextInput
                value={emoji}
                onChange={(event) => setEmoji(event.target.value.slice(0, 4))}
                className="w-20 text-center text-[21px]"
                maxLength={4}
              />
            </Field>
            <div className="min-w-0 flex-1">
              <Field label="Название">
                <TextInput
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Бассейн"
                  maxLength={32}
                  autoFocus
                />
              </Field>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setAdding(false)}
            className="text-text-muted tap-target mt-2 text-note"
          >
            Отмена
          </button>
        </Card>
      )}

      {custom.length > 0 && (
        <>
          <p className="text-text-muted mb-2 px-1 text-caption font-medium uppercase">
            Ваши плитки
          </p>
          <Card className="mb-4 space-y-1">
            {custom.map((tile) => (
              <Toggle
                key={tile.code}
                label={
                  <span className="flex items-center gap-2">
                    <span aria-hidden className="text-[18px] leading-none">
                      {tile.emoji}
                    </span>
                    {tile.title}
                  </span>
                }
                checked={visible.has(tile.code)}
                onChange={(next) => visibility.mutate({ code: tile.code, hidden: !next })}
              />
            ))}
          </Card>
        </>
      )}

      <p className="text-text-muted mb-2 px-1 text-caption font-medium uppercase">
        Стандартные
      </p>
      <Card className="space-y-1">
        {SYSTEM_TILES.map((tile) => (
          <Toggle
            key={tile.code}
            label={
              <span className="flex items-center gap-2">
                <Icon name={eventIcon(tile.code)} size={20} />
                {tile.title}
              </span>
            }
            checked={visible.has(tile.code)}
            onChange={(next) => visibility.mutate({ code: tile.code, hidden: !next })}
          />
        ))}
      </Card>

      <p className="text-text-muted mt-3 px-1 text-caption leading-snug">
        Выключённая плитка исчезает только у вашей семьи. Уже отмеченные события
        остаются в ленте.
      </p>
    </Sheet>
  )
}
